import { IDEMPOTENCY_HEADER, ROLE_HEADER, type ErrorResponse, type Role } from "@mubin/contracts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ErrorResponse | null,
  ) {
    super(body?.error.message ?? `HTTP ${status}`);
  }
}

const ROLE_KEY = "mubin.activeRole";
export function getStoredRole(): Role | null {
  try {
    return (localStorage.getItem(ROLE_KEY) as Role | null) ?? null;
  } catch {
    return null;
  }
}
export function storeRole(role: Role | null) {
  try {
    if (role) localStorage.setItem(ROLE_KEY, role);
    else localStorage.removeItem(ROLE_KEY);
  } catch {
    /* storage unavailable: the server falls back to the default role */
  }
}

/** Same-origin JSON request with the session cookie and the active role. */
export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const role = getStoredRole();
  if (role) headers.set(ROLE_HEADER, role);
  if (init.json !== undefined) headers.set("content-type", "application/json");
  const res = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body as ErrorResponse | null);
  return body as T;
}

/**
 * A state-changing request. POSTs carry an Idempotency-Key so a retried submit (double click, flaky
 * network) cannot create a second record; the key is fixed per call of send().
 */
export function send<T>(method: "POST" | "PATCH" | "PUT", path: string, json: unknown = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (method === "POST") headers[IDEMPOTENCY_HEADER] = crypto.randomUUID();
  return api<T>(path, { method, json, headers });
}

/** The server's validation message for a field, or its general message. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const issues = err.body?.error.details as { message?: string }[] | undefined;
    if (err.status === 400 && Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    if (err.status === 403) return "لا تملك صلاحية هذا الإجراء.";
    return err.message;
  }
  return "تعذر الاتصال بالخادم. حاول مرة أخرى.";
}
